import type { ApiClient } from './client.js';

/** A single class within a grade. `progress` = points in the jar so far. */
export interface TechTimeClassDto {
  id: string;
  teacherName: string;
  position: number;
  progress: number;
}

export interface TechTimeGradeDto {
  id: string;
  name: string;
  position: number;
  /** Manual jar-goal override in points; null = use the grade-name default. */
  goal: number | null;
  classes: TechTimeClassDto[];
}

/** Standalone class mutations echo the grade id so the client can place it. */
export type TechTimeClassWithGrade = TechTimeClassDto & { gradeId: string };

/** One recorded change to a class's jar progress (audit history). */
export interface TechTimeProgressEventDto {
  id: string;
  /** Points added by this change; negative for an undo or reset. */
  delta: number;
  /** Resulting jar total after the change. */
  progress: number;
  /** ISO timestamp of the change. */
  createdAt: string;
}

/**
 * Private Tech-Time tracker API (gated to the admin/sm2gk tenant server-side).
 * Grades group classes; each class tracks progress toward "GAMES" (free tech
 * time). Everything is owned by the calling teacher.
 */
export interface TechTimeApi {
  listGrades(): Promise<TechTimeGradeDto[]>;
  createGrade(name: string): Promise<TechTimeGradeDto>;
  renameGrade(gradeId: string, name: string): Promise<TechTimeGradeDto>;
  /** Set (or clear, with null) a grade's manual jar-goal override. */
  setGradeGoal(gradeId: string, goal: number | null): Promise<TechTimeGradeDto>;
  reorderGrades(orderedIds: string[]): Promise<void>;
  removeGrade(gradeId: string): Promise<void>;
  createClass(gradeId: string, teacherName: string): Promise<TechTimeClassWithGrade>;
  renameClass(classId: string, teacherName: string): Promise<TechTimeClassWithGrade>;
  reorderClasses(gradeId: string, orderedIds: string[]): Promise<void>;
  removeClass(classId: string): Promise<void>;
  /** Set a class's jar progress (absolute, 0..30). */
  setProgress(classId: string, progress: number): Promise<TechTimeClassWithGrade>;
  /** History of jar changes for a class, newest first. */
  listHistory(classId: string): Promise<TechTimeProgressEventDto[]>;
}

export function createTechTimeApi(client: ApiClient): TechTimeApi {
  return {
    async listGrades() {
      const res = await client.get<{ grades: TechTimeGradeDto[] }>('/api/tech-time/grades');
      return res.grades;
    },
    async createGrade(name) {
      const res = await client.post<{ grade: TechTimeGradeDto }>('/api/tech-time/grades', { name });
      return res.grade;
    },
    async renameGrade(gradeId, name) {
      const res = await client.patch<{ grade: TechTimeGradeDto }>(
        `/api/tech-time/grades/${gradeId}`,
        { name },
      );
      return res.grade;
    },
    async setGradeGoal(gradeId, goal) {
      const res = await client.put<{ grade: TechTimeGradeDto }>(
        `/api/tech-time/grades/${gradeId}/goal`,
        { goal },
      );
      return res.grade;
    },
    async reorderGrades(orderedIds) {
      await client.put('/api/tech-time/grades/order', { orderedIds });
    },
    async removeGrade(gradeId) {
      await client.delete(`/api/tech-time/grades/${gradeId}`);
    },
    async createClass(gradeId, teacherName) {
      const res = await client.post<{ class: TechTimeClassWithGrade }>(
        `/api/tech-time/grades/${gradeId}/classes`,
        { teacherName },
      );
      return res.class;
    },
    async renameClass(classId, teacherName) {
      const res = await client.patch<{ class: TechTimeClassWithGrade }>(
        `/api/tech-time/classes/${classId}`,
        { teacherName },
      );
      return res.class;
    },
    async reorderClasses(gradeId, orderedIds) {
      await client.put(`/api/tech-time/grades/${gradeId}/classes/order`, { orderedIds });
    },
    async removeClass(classId) {
      await client.delete(`/api/tech-time/classes/${classId}`);
    },
    async setProgress(classId, progress) {
      const res = await client.put<{ class: TechTimeClassWithGrade }>(
        `/api/tech-time/classes/${classId}/progress`,
        { progress },
      );
      return res.class;
    },
    async listHistory(classId) {
      const res = await client.get<{ events: TechTimeProgressEventDto[] }>(
        `/api/tech-time/classes/${classId}/history`,
      );
      return res.events;
    },
  };
}
